import React, { useEffect, useRef, useState } from "react";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const insights = [
    {
        id: 1,
        title: "Enhancing Customer Experience with AI",
        description: "A retail chain used AI personalization to better connect with customers, improving engagement and growing sales by 25%.",
        image: "/assets/what/c9.jpg",
        category: "Cloud Security"
    },
    {
        id: 2,
        title: "Modernizing Legacy Systems",
        description: "A logistics company upgraded its old systems into a modern digital platform, making operations smoother and boosting efficiency by 40%.",
        image: "/assets/home-/img2.png",
        category: "Strategy"
    },
    {
        id: 3,
        title: "Driving Sustainability With Cloud Engineering",
        description: "A food and beverage company cut energy costs by 20% while also strengthening its ESG goals using cloud-native applications.",
        image: "/assets/home-/img3.png",
        category: "Data Science"
    },
    {
        id: 4,
        title: "Optimizing Supply Chain With Cloud ERP",
        description: "A global manufacturer successfully reduced operational costs by 30% after we implemented a scalable Infor M3 ERP solution tailored to their needs.",
        image: "/assets/service-detail/img10.png",
        category: "Security"
    },
    {
        id: 5,
        title: "Accelerating Time-to-Market With DevOps",
        description: "A fintech startup was able to release products 50% faster after adopting our CI/CD and DevOps automation setup, making their development process smoother and more efficient.",
        image: "/assets/service-detail/img11.png",
        category: "Security"
    },
];

const LatestInsightsSection = () => {
    const [loaded, setLoaded] = useState(false);

    const featuredInsight = insights[0];
    const sliderInsights = insights.slice(1);
    const [sliderRef, instanceRef] = useKeenSlider(
        {
            initial: 0,
            created() {
                setLoaded(true);
            },
            loop: true,
            mode: "snap",
            slides: {
                perView: 1.1,
                spacing: 16,
            },
            breakpoints: {
                "(min-width: 640px)": { slides: { perView: 1.5, spacing: 20 } },
                "(min-width: 1024px)": { slides: { perView: 2, spacing: 15 } },
            },
        },
        [
            (slider) => {
                let timeout;
                let mouseOver = false;
                function clearNextTimeout() { clearTimeout(timeout); }
                function nextTimeout() {
                    clearTimeout(timeout);
                    if (mouseOver) return;
                    timeout = setTimeout(() => {
                        slider.next();
                    }, 2500);
                }
                slider.on("created", () => {
                    slider.container.addEventListener("mouseover", () => { mouseOver = true; clearNextTimeout(); });
                    slider.container.addEventListener("mouseout", () => { mouseOver = false; nextTimeout(); });
                    nextTimeout();
                });
                slider.on("dragStarted", clearNextTimeout);
                slider.on("animationEnded", nextTimeout);
                slider.on("updated", nextTimeout);
            },
        ]
    );

    return (
        <section className="pb-[40px] lg:pb-[80px] bg-white font-sans relative overflow-hidden">
            <div className="absolute inset-0 z-0 opacity-40 pointer-events-none">
                <FlowingMeshCanvasAlt />
            </div>

            <div className="max-w-[1220px] w-full mx-auto px-[20px] lg:px-[80px] relative z-10">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-[30px] lg:mb-[40px] gap-4">
                    <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] relative z-10 leading-tight">
                        Latest Insights
                    </h2>

                    {loaded && instanceRef.current && (
                        <div className="hidden md:flex items-center gap-4">
                            <NavButton onClick={(e) => e.stopPropagation() || instanceRef.current?.prev()} icon={<ArrowLeft size={20} />} />
                            <NavButton onClick={(e) => e.stopPropagation() || instanceRef.current?.next()} icon={<ArrowRight size={20} />} />
                        </div>
                    )}
                </div>
                
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-[21px]">
                    <div className="w-full lg:w-full max-w-full lg:max-w-[632px] flex-shrink-0">
                        <FeaturedInsightCard item={featuredInsight} />
                    </div>
                    <div className="w-full lg:w-full min-w-0">
                        <div ref={sliderRef} className="keen-slider h-full">
                            {sliderInsights.map((item) => (
                                <div key={item.id} className="keen-slider__slide h-full">
                                    <InsightCard item={item} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-[30px] lg:mt-[40px] flex justify-start md:justify-between items-center">
                    {loaded && instanceRef.current && (
                        <div className="flex md:hidden items-center gap-6">
                            <NavButton onClick={(e) => e.stopPropagation() || instanceRef.current?.prev()} icon={<ArrowLeft size={18} />} />
                            <NavButton onClick={(e) => e.stopPropagation() || instanceRef.current?.next()} icon={<ArrowRight size={18} />} />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

const FeaturedInsightCard = ({ item }) => {
    return (
        <div className="group relative h-[380px] md:h-[450px] w-full overflow-hidden shadow-lg rounded-sm">
            <img
                src={item.image}
                alt={item.title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.1)), linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.2))" }} />
            <div className="absolute bottom-0 left-0 flex w-full flex-col justify-end p-6 md:p-8 text-white">
                <h3 className="mb-2 md:mb-3 text-[22px] md:text-3xl font-bold leading-tight">
                    {item.title}
                </h3>
                <p className="mb-4 md:mb-6 line-clamp-2 text-[14px] md:text-base font-medium text-gray-200">
                    {item.description}
                </p>
                <Link to="/contact">
                    <div className="flex cursor-pointer items-center gap-2 text-sm md:text-base font-bold text-white transition-all hover:gap-3 uppercase tracking-wider">
                        Explore
                        <ArrowRight size={18} />
                    </div>
                </Link>
            </div>
        </div>
    );
};

const InsightCard = ({ item }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
        <motion.div
            initial="rest"
            animate={isHovered ? "hover" : "rest"}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => setIsHovered(!isHovered)}
            className="relative h-[400px] md:h-[450px] overflow-hidden cursor-pointer group shadow-sm hover:shadow-2xl transition-shadow duration-500 bg-white border border-gray-100 rounded-sm"
        >
            <div className="absolute inset-0 flex flex-col h-full z-10 bg-white">
                <div className="h-[200px] md:h-[240px] w-full overflow-hidden relative">
                    <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-700"
                        style={{ transform: isHovered ? 'scale(1.1)' : 'scale(1)' }}
                    />
                </div>
                <div className="p-6 md:p-[30px] flex flex-col justify-between flex-grow">
                    <h3 className="text-[#01386A] font-sora text-[18px] md:text-[22px] font-[700] leading-[1.3] line-clamp-3">
                        {item.title}
                    </h3>
                    <Link to="/contact">
                        <div className="flex items-center gap-2 text-[#01386A] underline font-[600] text-sm md:text-[15px] mt-auto">
                            Explore <ArrowUpRight size={16} />
                        </div>
                    </Link>
                </div>
            </div>
            <motion.div
                variants={{
                    rest: { opacity: 0, y: 20 },
                    hover: { opacity: 1, y: 0 }
                }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="absolute inset-0 z-20 bg-gradient-to-t from-[#001f40] via-[#01386A] to-[#01386A]/90 p-6 md:p-[30px] flex flex-col justify-end"
            >
                <div>
                    <h3 className="text-white font-sora text-[18px] md:text-[22px] font-[700] leading-[1.3] mb-3 md:mb-4">
                        {item.title}
                    </h3>
                    <motion.p
                        className="text-white/80 text-[14px] md:text-[16px] leading-[1.6] font-sans mb-6 md:mb-8"
                        variants={{
                            rest: { opacity: 0, height: 0 },
                            hover: { opacity: 1, height: "auto" }
                        }}
                    >
                        {item.description}
                    </motion.p>
                    <Link to="/contact">
                        <div className="flex items-center gap-2 text-white font-[600] text-sm md:text-[15px] uppercase tracking-wider">
                            Explore
                            <ChevronRight
                                size={18}
                                className={`transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`}
                            />
                        </div>
                    </Link>
                </div>
            </motion.div>
        </motion.div>
    );
};

const FlowingMeshCanvasAlt = () => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let animationFrameId;
        let time = 0;
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener("resize", resizeCanvas);
        resizeCanvas();
        const draw = () => {
            const { width, height } = canvas;
            ctx.clearRect(0, 0, width, height);
            const lines = 30;
            const amplitude = 90;
            const frequency = 0.002;
            const speed = 0.0025;
            ctx.lineWidth = 0.8;
            for (let i = 0; i < lines; i++) {
                ctx.beginPath();
                const alpha = 0.1 + (i / lines) * 0.25;
                ctx.strokeStyle = `hsla(217, 54%, 24%, ${alpha})`;
                for (let x = 0; x <= width; x += 30) {
                    const yOffset = height / 1.4;
                    const y1 = Math.sin(x * frequency - time + i * 0.05) * amplitude;
                    const y2 = Math.sin(x * frequency * 0.5 - time * 0.5) * (amplitude * 0.5);
                    const tilt = (x / width) * -150;
                    ctx.lineTo(x, yOffset + y1 + y2 + tilt + (i * 4));
                }
                ctx.stroke();
            }
            time += speed;
            animationFrameId = requestAnimationFrame(draw);
        };
        draw();
        return () => {
            window.removeEventListener("resize", resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);
    return <canvas ref={canvasRef} className="w-full h-full" />;
};

const NavButton = ({ onClick, icon }) => (
    <button
        onClick={onClick}
        className="w-[36px] h-[36px] md:w-[40px] md:h-[40px] rounded-full border border-[#ED6800] text-[#ED6800] flex items-center justify-center hover:bg-[#ED6800] hover:text-white transition-all duration-300 active:scale-95"
    >
        {icon}
    </button>
);

export default LatestInsightsSection;